import { useEffect, useRef, useState } from 'react';
import { toast } from '@doxyme/ui/components';
import { _getLogger } from '@doxyme/analytics';
import { isProduction } from '../../../shared/isProduction';
import { applyPendingSideloadParams } from '../../../features/extensions/sideloading';

const logger = _getLogger('useExtensionsSideloading', {
  component: 'extensions',
});

export interface UseExtensionsSideloadingResult {
  sideloadedNames: Set<string>;
  isComplete: boolean;
}

/**
 * Hook that applies extension sideload params captured by static-site-router.
 *
 * Initial URL hash/query sideload values are parsed on the router page, stashed,
 * and the app reloads to a clean path before this hook runs.
 *
 * Completely disabled on production for security.
 *
 * @returns Set of sideloaded extension names and completion status
 */
export function useExtensionsSideloading(): UseExtensionsSideloadingResult {
  const [sideloadedNames, setSideloadedNames] = useState<Set<string>>(
    new Set(),
  );
  const [isComplete, setIsComplete] = useState(false);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    // Disabled on production for security
    if (isProduction()) {
      setIsComplete(true);
      logger.warn(
        '[useExtensionsSideloading] Sideloading is disabled on production',
      );
      return;
    }

    const results = applyPendingSideloadParams();

    if (results.length === 0) {
      setIsComplete(true);
      return;
    }

    logger.info('[useExtensionsSideloading] Processing sideload params', {
      count: results.length,
    });

    const names = new Set<string>();

    for (const result of results) {
      if (result.status === 'success') {
        names.add(result.name);
        toast.success({
          title: `Sideloaded: ${result.name}`,
        });
        continue;
      }

      if (result.status === 'process_error') {
        toast.error({
          title: `Failed to sideload: ${result.manifestName}. Check the console.`,
        });
        continue;
      }

      toast.error({
        title: 'Failed to sideload extension. Check the console for details.',
      });
    }

    setSideloadedNames(names);
    setIsComplete(true);
  }, []);

  return { sideloadedNames, isComplete };
}
